import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { questions } from '../data/questions';

const { FiArrowLeft, FiArrowRight, FiClock, FiHelpCircle } = FiIcons;

function Screener({ answers, setAnswers, onShowResults }) {
  const [currentQuestion, setCurrentQuestion] = useState(0);

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleNext = () => {
    const question = questions[currentQuestion];
    
    if (!answers[question.id]) {
      alert('Please select an answer before continuing.');
      return;
    }
    
    if (currentQuestion === questions.length - 1) {
      const phase = determinePhase(answers);
      onShowResults(phase);
    } else {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const determinePhase = (answers) => {
    if (answers[2] === 'none' || answers[4] === 'no') {
      return "pre-alphabetic";
    } else if (answers[3] === 'no' || answers[4] === 'partial') {
      return "partial-alphabetic";
    } else if (answers[6] === 'no' || answers[7] === 'no') {
      return "full-alphabetic";
    } else if (answers[7] === 'partial' || answers[6] === 'partial') {
      return "consolidated-alphabetic";
    } else {
      return "automatic";
    }
  };

  const question = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <motion.div 
      className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-brand-sage/20 p-8 mb-6"
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <span className="text-sm font-medium text-brand-dark-blue bg-brand-sage-light px-3 py-1 rounded-full">
            Question {currentQuestion + 1} of {questions.length}
          </span>
          <div className="flex items-center gap-1 text-sm font-medium text-brand-teal">
            <SafeIcon icon={FiClock} className="h-4 w-4" />
            ~5 minutes
          </div>
        </div>
        
        <div className="w-full bg-brand-sage-light rounded-full h-3 overflow-hidden">
          <motion.div 
            className="bg-gradient-to-r from-brand-teal to-brand-dark-blue h-3 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          />
        </div>
      </div>
      
      <motion.div 
        key={currentQuestion}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="mb-8"
      >
        <div className="flex items-start gap-3 mb-6">
          <h3 className="text-xl font-semibold text-brand-dark-blue leading-relaxed flex-1">
            {question.text}
          </h3>
          {question.tooltip && (
            <div className="group relative">
              <SafeIcon 
                icon={FiHelpCircle} 
                className="h-5 w-5 text-brand-teal hover:text-brand-teal-dark cursor-help transition-colors" 
              />
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none w-64 z-10">
                {question.tooltip}
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
              </div>
            </div>
          )}
        </div>
        
        <div className="space-y-3">
          {question.options.map((option, index) => (
            <motion.label 
              key={option.value}
              className={`flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                answers[question.id] === option.value
                  ? 'border-brand-teal bg-brand-teal/5 shadow-md'
                  : 'border-brand-sage hover:border-brand-teal/50 hover:bg-brand-sage-light/30'
              }`}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1, duration: 0.3 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <input
                type="radio"
                name={`question-${question.id}`}
                value={option.value}
                checked={answers[question.id] === option.value}
                onChange={() => handleAnswer(question.id, option.value)}
                className="h-5 w-5 text-brand-teal focus:ring-brand-teal border-brand-sage"
              />
              <span className="ml-4 text-gray-800 font-medium">{option.text}</span>
            </motion.label>
          ))}
        </div>
      </motion.div>
      
      <div className="flex justify-between">
        <motion.button
          onClick={handlePrev}
          className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${
            currentQuestion > 0
              ? 'bg-brand-sage hover:bg-brand-sage-dark text-brand-dark-blue hover:shadow-md'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
          disabled={currentQuestion === 0}
          whileHover={currentQuestion > 0 ? { scale: 1.05 } : {}}
          whileTap={currentQuestion > 0 ? { scale: 0.95 } : {}}
        >
          <SafeIcon icon={FiArrowLeft} className="h-4 w-4" />
          Back
        </motion.button>
        
        <motion.button
          onClick={handleNext}
          className="flex items-center gap-2 bg-gradient-to-r from-brand-teal to-brand-dark-blue hover:from-brand-teal-dark hover:to-brand-dark-blue text-white font-semibold px-6 py-3 rounded-xl shadow-lg transition-all duration-200 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-brand-teal/30"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          {currentQuestion === questions.length - 1 ? 'See Results' : 'Next'}
          <SafeIcon icon={FiArrowRight} className="h-4 w-4" />
        </motion.button>
      </div>
    </motion.div>
  );
}

export default Screener;